import { useEffect, useState } from "react";
import React from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function getRecordTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    'lab_result': 'Resultado Laboratorial',
    'lab_exam': 'Exame Laboratorial',
    'results': 'Resultados',
    'consulta': 'Consulta',
    'clinical_note': 'Nota Clínica',
    'clinical_observation': 'Observação Clínica',
    'anamnesis': 'Anamnese',
    'physiotherapy_evolution': 'Evolução Fisioterapia',
    'nutritional_assessment': 'Avaliação Nutricional',
    'meal_plan': 'Plano Alimentar',
    'training_plan': 'Plano de Treino',
    'body_composition': 'Composição Corporal',
    'psychotherapy_session': 'Sessão Psicoterapia',
    'aesthetic_protocol': 'Protocolo Estético',
    'vitals': 'Sinais Vitais',
    'chiropractic_adjustment': 'Ajuste Quiroprático',
  };
  return labels[type] || type;
}
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, FileText, Sparkles, Eye, ChevronLeft, ChevronRight, Download, ExternalLink, User, Trash2, Edit2, Save, X } from "lucide-react";
import { AnalysisCard } from "./AnalysisCard";
import { RecordDetails } from "./RecordDetails";
import { SmartfileSessionDetails } from "./SmartfileSessionDetails";
import { PatientSmartfileRecordsList } from "./PatientSmartfileRecordsList";
import { isAIDisabled } from "@/lib/smartfile-ai-preference";

interface StructuredRecord {
  type?: string;
  [key: string]: any;
}

interface DocumentData {
  id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  content_type?: string | null;
  status?: string | null;
  extracted_text?: string | null;
  ai_summary?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ai_insights?: any;
  ai_risk_level?: string | null;
  professional_notes?: string | null;
  structured_records?: StructuredRecord[] | null;
  temp_patient_data?: any;
  created_at?: string;
  patient_id?: string;
}

interface DocumentAnalysisModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentId: string;
  documentType: 'smartfile' | 'document';
  patientId: string;
}

export function DocumentAnalysisModal({
  isOpen,
  onClose,
  documentId,
  documentType,
  patientId
}: DocumentAnalysisModalProps) {
  const [document, setDocument] = useState<DocumentData | null>(null);
  const [records, setRecords] = useState<any[]>([]);
  const [labResults, setLabResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [regeneratingInsights, setRegeneratingInsights] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("summary");
  const [isEditingText, setIsEditingText] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [isSavingText, setIsSavingText] = useState(false);

  // Para smartfile sessions, vamos usar structured_records diretamente
  const hasStructuredRecords = React.useMemo(() => {
    return documentType === 'smartfile' &&
      document?.structured_records &&
      Array.isArray(document.structured_records) &&
      document.structured_records.some((r) => {
        return r.type === 'lab_result' || r.type === 'training_plan' || r.type === 'physiotherapy_evolution' || r.type === 'anthropometry';
      });
  }, [documentType, document?.structured_records]);

  useEffect(() => {
    if (isOpen && documentId) {
      loadDocumentData();
    } else if (!isOpen) {
      // Reset edit state when modal closes
      setIsEditingText(false);
      setEditedText("");
    }
  }, [isOpen, documentId, documentType]);

  async function loadDocumentData() {
    try {
      setLoading(true);

      if (documentType === 'smartfile') {
        const { data: smartfile } = await supabase
          .from("smartfile_sessions")
          .select("*")
          .eq("id", documentId)
          .single();

        if (smartfile) {
          // Converter structured_records se necessário
          let structuredRecords: any[] | null = null;
          if (smartfile.structured_records) {
            if (Array.isArray(smartfile.structured_records)) {
              structuredRecords = smartfile.structured_records;
            } else if (typeof smartfile.structured_records === 'string') {
              try {
                const parsed = JSON.parse(smartfile.structured_records);
                structuredRecords = Array.isArray(parsed) ? parsed : null;
              } catch {
                structuredRecords = null;
              }
            }
          }

          setDocument({
            ...smartfile,
            structured_records: structuredRecords as StructuredRecord[] | null
          } as DocumentData);
        }

        // ✅ REMOVIDO: patient_health_records não é mais usado
        setRecords([]);

        // Buscar lab results (para documentos antigos)
        const { data: labData } = await supabase
          .from("lab_results_onboarding")
          .select(`
          *,
          subcategory:lab_subcategories(name)
        `)
          .eq("source_session_id", documentId)
          .order("collected_at", { ascending: false });

        setLabResults(labData || []);
      } else {
        const { data: doc } = await supabase
          .from("documents_onboarding")
          .select("*")
          .eq("id", documentId)
          .single();

        setDocument(doc);

        // ✅ REMOVIDO: patient_health_records não é mais usado
        setRecords([]);

        // Buscar lab results para documents
        const { data: labData } = await supabase
          .from("lab_results_onboarding")
          .select(`
          *,
          subcategory:lab_subcategories(name)
        `)
          .eq("source_document_id", documentId)
          .order("collected_at", { ascending: false });

        setLabResults(labData || []);
      }
    } catch (error) {
      console.error("Error loading document:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleOpenOriginalFile() {
    if (!document) return;

    try {
      const bucket = 'patient-docs';
      const filePath = document.file_path;

      console.log('[handleOpenOriginalFile]', { documentType, bucket, filePath });

      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(filePath, 3600);

      if (error) {
        console.error('Error creating signed URL:', error);
        toast.error("Erro ao abrir arquivo: " + error.message);
        return;
      }

      if (data?.signedUrl) {
        window.open(data.signedUrl, "_blank");
      } else {
        toast.error("URL do arquivo não disponível");
      }
    } catch (error) {
      console.error("Error opening file:", error);
      toast.error("Erro ao abrir arquivo");
    }
  }

  async function handleDeleteDocument() {
    try {
      if (documentType === 'smartfile') {
        await supabase.from('smartfile_sessions').delete().eq('id', documentId);
      } else {
        await supabase.from('documents_onboarding').delete().eq('id', documentId);
      }

      toast.success("Documento excluído com sucesso!");
      onClose();
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error("Erro ao excluir documento");
    }
  }

  async function handleRegenerateInsights() {
    if (!document || !documentId) return;
    
    if (isAIDisabled()) {
      toast.info("Resumo e Insights da IA estão desativados. Ative no Dashboard para gerar insights.");
      return;
    }

    try {
      setRegeneratingInsights(true);

      const { data, error } = await supabase.functions.invoke(
        'smartfile-v15-generate-insights-fast',
        {
          body: {
            records: document.structured_records || [],
            contentType: document.content_type,
            sessionId: document.id, // usar document.id como sessionId se disponível
            patientData: document.temp_patient_data
          }
        }
      );
      
      if (data?.aiModel) {
        console.log(`[DocumentAnalysis] 🤖 Insights AI Model: ${data.aiModel.provider}/${data.aiModel.model_code} (${data.aiModel.name || 'N/A'})`);
      }

      if (error) throw error;

      const { error: updateError } = await supabase
        .from('smartfile_sessions')
        .update({
          ai_summary: data.summary,
          ai_insights: data.insights
        })
        .eq('id', documentId);

      if (updateError) throw updateError;

      toast.success("Nova análise gerada com sucesso!");
      await loadDocumentData();
    } catch (error) {
      console.error('Error regenerating insights:', error);
      toast.error("Erro ao gerar nova análise");
    } finally {
      setRegeneratingInsights(false);
    }
  }

  function handleStartEditText() {
    setEditedText(document.extracted_text || "");
    setIsEditingText(true);
  }

  function handleCancelEditText() {
    setIsEditingText(false);
    setEditedText("");
  }

  async function handleSaveText() {
    if (!document || !documentId) return;

    try {
      setIsSavingText(true);

      const tableName = documentType === 'smartfile' ? 'smartfile_sessions' : 'documents_onboarding';

      const { error } = await supabase
        .from(tableName)
        .update({
          extracted_text: editedText,
          updated_at: new Date().toISOString()
        })
        .eq('id', documentId);

      if (error) throw error;

      setDocument({ ...document, extracted_text: editedText });
      setIsEditingText(false);
      toast.success("Texto salvo com sucesso!");
    } catch (error) {
      console.error('Error saving text:', error);
      toast.error("Erro ao salvar texto");
    } finally {
      setIsSavingText(false);
    }
  }

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : document ? (
          <>
            <DialogHeader>
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <DialogTitle className="text-base sm:text-lg break-words pr-2">
                      {document.file_name}
                    </DialogTitle>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {document.file_type?.toUpperCase()}
                      </Badge>
                      {documentType === 'smartfile' && document.content_type && (
                        <Badge className="bg-purple-50 text-purple-700 border-purple-200 text-xs">
                          {(() => {
                            const types: Record<string, string> = {
                              'anamnesis': 'Anamnese',
                              'results': 'Resultados',
                              'vitals': 'Sinais Vitais',
                              'prescription': 'Prescrição',
                              'report': 'Relatório',
                              'biopsy_report': 'Biópsia',
                              'anatomopathology': 'Anatomia Patológica',
                              'physiotherapy_evolution': 'Evolução Fisioterápica',
                              'training_plan': 'Plano de Treino',
                              'meal_plan': 'Plano Alimentar',
                              'other': 'Outro'
                            };
                            return types[document.content_type] || document.content_type;
                          })()}
                        </Badge>
                      )}
                      {document.status && (
                        <Badge variant="secondary" className="text-xs">
                          {document.status}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOpenOriginalFile}
                    className="gap-2 shrink-0 w-full sm:w-auto"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span className="hidden sm:inline">Abrir Original</span>
                    <span className="sm:hidden">Abrir</span>
                  </Button>
                </div>
              </div>
            </DialogHeader>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              {/* Mobile: Badges horizontais com scroll */}
              <div className="lg:hidden mb-4">
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6 max-w-[300px]">
                  <Badge
                    variant={activeTab === "summary" ? "default" : "outline"}
                    className={cn(
                      "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap",
                      activeTab === "summary"
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "hover:bg-accent"
                    )}
                    onClick={() => setActiveTab("summary")}
                  >
                    Resumo
                  </Badge>
                  <Badge
                    variant={activeTab === "analysis" ? "default" : "outline"}
                    className={cn(
                      "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap",
                      activeTab === "analysis"
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "hover:bg-accent"
                    )}
                    onClick={() => setActiveTab("analysis")}
                  >
                    Análise IA
                  </Badge>
                  <Badge
                    variant={activeTab === "text" ? "default" : "outline"}
                    className={cn(
                      "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap",
                      activeTab === "text"
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "hover:bg-accent"
                    )}
                    onClick={() => setActiveTab("text")}
                  >
                    Texto
                  </Badge>
                  <Badge
                    variant={activeTab === "records" ? "default" : "outline"}
                    className={cn(
                      "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap",
                      activeTab === "records"
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "hover:bg-accent"
                    )}
                    onClick={() => setActiveTab("records")}
                  >
                    Registros ({documentType === 'smartfile' && hasStructuredRecords
                      ? 1
                      : records.length})
                  </Badge>
                  <Badge
                    variant={activeTab === "results" ? "default" : "outline"}
                    className={cn(
                      "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap",
                      activeTab === "results"
                        ? "bg-primary text-primary-foreground hover:bg-primary/90"
                        : "hover:bg-accent"
                    )}
                    onClick={() => setActiveTab("results")}
                  >
                    Resultados ({documentType === 'smartfile' && hasStructuredRecords
                      ? (document?.structured_records?.filter((r: any) =>
                        typeof r === 'object' && r !== null && 'type' in r && r.type === 'lab_result'
                      ).length || 0)
                      : labResults.length})
                  </Badge>
                </div>
              </div>

              {/* Desktop: TabsList padrão */}
              <TabsList className="hidden lg:grid w-full grid-cols-5 gap-1 h-auto">
                <TabsTrigger value="summary" onClick={(e) => e.stopPropagation()}>Resumo</TabsTrigger>
                <TabsTrigger value="analysis" onClick={(e) => e.stopPropagation()}>Análise IA</TabsTrigger>
                <TabsTrigger value="text" onClick={(e) => e.stopPropagation()}>Texto</TabsTrigger>
                <TabsTrigger value="records" onClick={(e) => e.stopPropagation()}>
                  Registros ({documentType === 'smartfile' && hasStructuredRecords
                    ? 1
                    : records.length})
                </TabsTrigger>
                <TabsTrigger value="results" onClick={(e) => e.stopPropagation()}>
                  Resultados ({documentType === 'smartfile' && hasStructuredRecords
                    ? (document?.structured_records?.filter((r: any) =>
                      typeof r === 'object' && r !== null && 'type' in r && r.type === 'lab_result'
                    ).length || 0)
                    : labResults.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="summary" className="space-y-4">
                {document.ai_summary && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="rounded-lg bg-muted/50 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="h-4 w-4 text-primary" />
                          <span className="font-medium text-sm">Resumo IA</span>
                        </div>
                        <p className="text-sm leading-relaxed">{document.ai_summary}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {documentType === 'smartfile' && document.professional_notes && (
                  <Card>
                    <CardContent className="p-4">
                      <div className="rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900 p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                          <span className="font-medium text-sm text-blue-700 dark:text-blue-400">Análise do Profissional</span>
                        </div>
                        <p className="text-sm leading-relaxed text-blue-900 dark:text-blue-100 whitespace-pre-wrap">{document.professional_notes}</p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="analysis">
                {(() => {
                  console.log('[DocumentAnalysisModal] Analysis tab:', {
                    hasAiSummary: !!document.ai_summary,
                    hasAiInsights: !!document.ai_insights,
                    aiInsightsType: typeof document.ai_insights,
                    aiInsightsLength: Array.isArray(document.ai_insights) ? document.ai_insights.length : 0,
                    firstInsight: Array.isArray(document.ai_insights) && document.ai_insights[0]
                      ? { type: document.ai_insights[0].type, title: document.ai_insights[0].title }
                      : null,
                    structuredRecordsCount: document.structured_records?.length || 0
                  });

                  const disableAI = isAIDisabled();
                  const hasAnalysisData = !disableAI && (
                    document.ai_summary ||
                    (document.ai_insights &&
                      (Array.isArray(document.ai_insights) ? document.ai_insights.length > 0 : true))
                  );

                  if (disableAI) {
                    return (
                      <Card>
                        <CardContent className="py-12 text-center text-sm text-muted-foreground">
                          <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <p>Resumo e Insights da IA estão desativados</p>
                          <p className="text-xs mt-2">Ative no Dashboard para visualizar análises geradas pela IA.</p>
                        </CardContent>
                      </Card>
                    );
                  }

                  return hasAnalysisData ? (
                    <AnalysisCard
                      summary={document.ai_summary}
                      insights={document.ai_insights}
                      riskLevel={document.ai_risk_level}
                      onRegenerate={documentType === 'smartfile' && document.structured_records ? handleRegenerateInsights : undefined}
                      isRegenerating={regeneratingInsights}
                    />
                  ) : (
                    <Card>
                      <CardContent className="py-12 text-center text-sm text-muted-foreground">
                        <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>Nenhuma análise IA disponível para este documento</p>
                        <p className="text-xs mt-2">O documento pode estar em processamento ou não possui análise gerada.</p>
                      </CardContent>
                    </Card>
                  );
                })()}
              </TabsContent>

              <TabsContent value="text">
                <Card>
                  <CardContent className="p-4 sm:p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-medium">Texto Extraído</h3>
                        {!isEditingText && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleStartEditText}
                            className="gap-2"
                          >
                            <Edit2 className="h-4 w-4" />
                            {document.extracted_text ? "Editar" : "Adicionar Texto"}
                          </Button>
                        )}
                      </div>

                      {isEditingText ? (
                        <div className="space-y-3">
                          <Textarea
                            value={editedText}
                            onChange={(e) => setEditedText(e.target.value)}
                            placeholder="Digite o texto extraído..."
                            className="min-h-[400px] font-mono text-sm sm:text-base leading-relaxed"
                            disabled={isSavingText}
                          />
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleCancelEditText}
                              disabled={isSavingText}
                              className="gap-2"
                            >
                              <X className="h-4 w-4" />
                              Cancelar
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveText}
                              disabled={isSavingText || editedText === (document.extracted_text || "")}
                              className="gap-2"
                            >
                              {isSavingText ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  Salvando...
                                </>
                              ) : (
                                <>
                                  <Save className="h-4 w-4" />
                                  Salvar
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : document.extracted_text ? (
                        <div className="overflow-x-auto -mx-4 px-4 sm:-mx-6 sm:px-6 max-w-[320px] sm:max-w-2xl mx-auto">
                          <pre className="text-sm sm:text-base whitespace-pre-wrap font-mono leading-relaxed break-words overflow-wrap-anywhere">
                            {document.extracted_text}
                          </pre>
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground text-center py-8">
                          Nenhum texto extraído disponível. Clique em "Adicionar Texto" para adicionar.
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="records">
                {documentType === 'smartfile' && hasStructuredRecords ? (
                  // Mostrar lista de registros (sessões) como na tela principal
                  <PatientSmartfileRecordsList
                    sessions={[document as any]}
                    onRefresh={async () => {
                      await loadDocumentData();
                    }}
                  />
                ) : records.length > 0 ? (
                  // Mostrar registros gerados (abordagem antiga)
                  <div className="space-y-3">
                    {records.map((record) => (
                      <Card key={record.id}>
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-sm">{record.title}</CardTitle>
                              <div className="flex items-center gap-2 mt-2">
                                <p className="text-xs text-muted-foreground">
                                  {new Date(record.observed_at).toLocaleDateString("pt-BR", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit"
                                  })}
                                </p>
                                <Badge variant="outline" className="text-xs">
                                  {getRecordTypeLabel(record.type)}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </CardHeader>

                        {record.ai_summary && (
                          <CardContent className="pt-0 pb-3 border-b">
                            <div className="bg-muted/30 rounded-lg p-3">
                              <p className="text-xs font-medium text-muted-foreground mb-1">📝 Resumo</p>
                              <p className="text-sm text-muted-foreground">{record.ai_summary}</p>
                            </div>
                          </CardContent>
                        )}

                        <RecordDetails record={record} />
                      </Card>
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center text-sm text-muted-foreground">
                      {documentType === 'smartfile'
                        ? "Nenhum registro estruturado encontrado nesta sessão"
                        : "Nenhum registro gerado a partir deste documento"}
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="results">
                {documentType === 'smartfile' && hasStructuredRecords ? (
                  // Para smartfile sessions, mostrar structured_records como resultados (mesma estrutura de registros)
                  <PatientSmartfileRecordsList
                    sessions={[document as any]}
                    onRefresh={async () => {
                      await loadDocumentData();
                    }}
                  />
                ) : labResults.length > 0 ? (
                  <Card>
                    <CardContent className="p-0">
                      <div className="overflow-x-auto -mx-4 sm:mx-0">
                        <div className="inline-block min-w-full align-middle">
                          <div className="overflow-hidden">
                            <table className="min-w-full divide-y divide-border">
                              <thead className="bg-muted/50 border-b">
                                <tr>
                                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Data</th>
                                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">Exame</th>
                                  <th className="px-3 sm:px-4 py-3 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">Valor</th>
                                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap hidden sm:table-cell">Unidade</th>
                                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap hidden md:table-cell">Referência</th>
                                  <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap hidden sm:table-cell">Categoria</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-border bg-background">
                                {labResults.map((result) => (
                                  <tr key={result.id} className="hover:bg-muted/30">
                                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm whitespace-nowrap">
                                      {result.collected_at
                                        ? new Date(result.collected_at).toLocaleDateString("pt-BR")
                                        : "–"}
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm font-medium max-w-[150px] sm:max-w-none truncate">{result.test_name}</td>
                                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-right font-semibold whitespace-nowrap">
                                      {result.value !== null ? result.value : "–"}
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-muted-foreground whitespace-nowrap hidden sm:table-cell">
                                      {result.unit || "–"}
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 text-xs sm:text-sm text-muted-foreground whitespace-nowrap hidden md:table-cell">
                                      {result.ref_low && result.ref_high
                                        ? `${result.ref_low} - ${result.ref_high}`
                                        : "–"}
                                    </td>
                                    <td className="px-3 sm:px-4 py-3 hidden sm:table-cell">
                                      {result.subcategory?.name ? (
                                        <Badge variant="outline" className="text-xs">
                                          {result.subcategory.name}
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">Sem categoria</span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center text-sm text-muted-foreground">
                      Nenhum resultado laboratorial gerado a partir deste documento
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <div className="py-12 text-center text-sm text-muted-foreground">
            Documento não encontrado
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
